import { useState, useEffect } from 'react';
import {
  Users, Megaphone, CalendarDays, Plus, Trash2, Pencil, Loader2, Mail,
  Pin, MapPin, Clock, ExternalLink, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { orgApi } from '../../utils/apiV2';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { NativeSelect } from '../ui/native-select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import ServiceToggle from '../ServiceToggle';

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const toLocalIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseIso = (s) => {
  if (!s) return undefined;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const DateField = ({ value, onChange, placeholder, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="w-full h-9 px-3 rounded-lg bg-white shadow-sm border border-input text-sm text-left flex items-center justify-between gap-2 hover:bg-gray-50 transition-colors">
          <span className={value ? 'text-gray-900' : 'text-gray-400'}>{value ? fmtDate(value) : placeholder}</span>
          <CalendarDays className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={parseIso(value)}
          onSelect={(d) => { onChange(d ? toLocalIso(d) : ''); setOpen(false); }} initialFocus />
      </PopoverContent>
    </Popover>
  );
};

const STATUS_CLS = {
  active: 'bg-green-100 text-green-700',
  past_due: 'bg-red-100 text-red-700',
  paused: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-gray-100 text-gray-500',
  pending: 'bg-gray-100 text-gray-600',
};

const SUB_TABS = [
  { id: 'members', label: 'Members', icon: Users },
  { id: 'announcements', label: 'Announcements', icon: Megaphone },
  { id: 'events', label: 'Event Calendar', icon: CalendarDays },
];

const EMPTY_EVENT = {
  title: '', description: '', category: '', startDate: '', endDate: '', startTime: '',
  endTime: '', location: '', capacity: '', membersOnly: false, serviceId: '', active: true,
};

export const MembersTab = () => {
  const [subTab, setSubTab] = useState('members');
  const [members, setMembers] = useState(null);
  const [announcements, setAnnouncements] = useState(null);
  const [events, setEvents] = useState(null);
  const [services, setServices] = useState([]);
  const [orgSlug, setOrgSlug] = useState('');

  // Announcement composer
  const [annTitle, setAnnTitle] = useState('');
  const [annBody, setAnnBody] = useState('');
  const [annEmail, setAnnEmail] = useState(false);
  const [annBusy, setAnnBusy] = useState(false);

  // Event dialog
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState(EMPTY_EVENT);
  const [eventBusy, setEventBusy] = useState(false);

  // RSVP list dialog
  const [rsvpListEvent, setRsvpListEvent] = useState(null);
  const [rsvpList, setRsvpList] = useState(null);

  const openRsvps = (e) => {
    setRsvpListEvent(e);
    setRsvpList(null);
    orgApi.getClubEventRsvps(e.id).then((r) => setRsvpList(r.data?.rsvps || [])).catch(() => setRsvpList([]));
  };

  useEffect(() => {
    orgApi.getMembers().then((r) => setMembers(r.data)).catch(() => setMembers({ members: [], total: 0, activeCount: 0 }));
    orgApi.getAnnouncements().then((r) => setAnnouncements(r.data?.announcements || [])).catch(() => setAnnouncements([]));
    orgApi.getClubEvents().then((r) => setEvents(r.data?.events || [])).catch(() => setEvents([]));
    orgApi.getServices({ activeOnly: true }).then((r) => setServices(r.data?.services || r.data || [])).catch(() => {});
    orgApi.getOrganization().then((r) => setOrgSlug(r.data?.slug || r.data?.organization?.slug || '')).catch(() => {});
  }, []);

  const postAnnouncement = async () => {
    if (!annTitle.trim() || annTitle.trim().length < 2) { toast.error('Give the announcement a title'); return; }
    setAnnBusy(true);
    try {
      const res = await orgApi.createAnnouncement({ title: annTitle.trim(), body: annBody.trim() || null, sendEmail: annEmail });
      setAnnouncements((l) => [res.data, ...(l || [])]);
      toast.success(annEmail ? `Posted — emailed to ${res.data.emailRecipientCount} members` : 'Announcement posted');
      setAnnTitle(''); setAnnBody(''); setAnnEmail(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not post the announcement');
    } finally { setAnnBusy(false); }
  };

  const removeAnnouncement = async (a) => {
    try {
      await orgApi.deleteAnnouncement(a.id);
      setAnnouncements((l) => l.filter((x) => x.id !== a.id));
    } catch { toast.error('Could not delete'); }
  };

  const togglePin = async (a) => {
    try {
      await orgApi.updateAnnouncement(a.id, { pinned: !a.pinned });
      setAnnouncements((l) => l.map((x) => (x.id === a.id ? { ...x, pinned: !a.pinned } : x)));
    } catch { toast.error('Could not update'); }
  };

  const openCreateEvent = () => { setEditingEvent(null); setEventForm(EMPTY_EVENT); setShowEventDialog(true); };
  const openEditEvent = (e) => {
    setEditingEvent(e);
    setEventForm({
      title: e.title, description: e.description || '', category: e.category || '',
      startDate: e.startDate || '', endDate: e.endDate || '', startTime: e.startTime || '',
      endTime: e.endTime || '', location: e.location || '', capacity: e.capacity || '',
      membersOnly: !!e.membersOnly, serviceId: e.serviceId || '', active: e.active !== false,
    });
    setShowEventDialog(true);
  };

  const saveEvent = async () => {
    if (!eventForm.title.trim() || eventForm.title.trim().length < 2) { toast.error('Give the event a title'); return; }
    if (!eventForm.startDate) { toast.error('Pick the event date'); return; }
    setEventBusy(true);
    try {
      const body = {
        title: eventForm.title.trim(),
        description: eventForm.description.trim() || null,
        category: eventForm.category.trim() || null,
        startDate: eventForm.startDate,
        endDate: eventForm.endDate || null,
        startTime: eventForm.startTime || null,
        endTime: eventForm.endTime || null,
        location: eventForm.location.trim() || null,
        capacity: eventForm.capacity ? Number(eventForm.capacity) : null,
        membersOnly: eventForm.membersOnly,
        serviceId: eventForm.serviceId || null,
        active: eventForm.active,
      };
      if (editingEvent) {
        const res = await orgApi.updateClubEvent(editingEvent.id, body);
        setEvents((l) => l.map((x) => (x.id === editingEvent.id ? res.data : x)));
        toast.success('Event updated');
      } else {
        const res = await orgApi.createClubEvent(body);
        setEvents((l) => [...(l || []), res.data].sort((a, b) => (a.startDate || '').localeCompare(b.startDate || '')));
        toast.success('Event added to the calendar');
      }
      setShowEventDialog(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the event');
    } finally { setEventBusy(false); }
  };

  const removeEvent = async (e) => {
    try {
      await orgApi.deleteClubEvent(e.id);
      setEvents((l) => l.filter((x) => x.id !== e.id));
    } catch { toast.error('Could not delete'); }
  };

  const toggleEventActive = async (e) => {
    try {
      await orgApi.updateClubEvent(e.id, { active: !e.active });
      setEvents((l) => l.map((x) => (x.id === e.id ? { ...x, active: !e.active } : x)));
    } catch { toast.error('Could not update'); }
  };

  return (
    <div data-testid="members-tab">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Members</h2>
          <p className="text-sm text-gray-500 mt-1">
            Your club — everyone with a subscription. Post announcements, email members, and run your event calendar.
          </p>
        </div>
        {orgSlug && (
          <a href={`/b/${orgSlug}/events`} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700"
             data-testid="members-public-calendar-link">
            <ExternalLink className="w-4 h-4" /> Public event calendar
          </a>
        )}
      </div>

      <div className="flex items-center gap-2 mb-6 overflow-x-auto">
        {SUB_TABS.map((t) => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              subTab === t.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 shadow-sm hover:bg-gray-50'}`}
            data-testid={`members-subtab-${t.id}`}>
            <t.icon className="w-4 h-4" /> {t.label}
            {t.id === 'members' && members ? <span className="text-xs opacity-70">({members.members?.length ?? members.activeCount})</span> : null}
          </button>
        ))}
      </div>

      {/* ── Members list ── */}
      {subTab === 'members' && (
        members === null ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        : members.members.length === 0 ? (
          <div className="bg-gray-50 rounded-2xl p-10 text-center" data-testid="members-empty-state">
            <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-600 font-medium">No members yet</p>
            <p className="text-xs text-gray-500 mt-1">Members appear here automatically when customers subscribe to one of your subscription services.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(2,12,27,0.06)] overflow-hidden" data-testid="members-list">
            {members.members.map((m, i) => (
              <div key={m.email || i} className={`px-5 py-4 flex flex-wrap items-center gap-3 ${i > 0 ? 'border-t border-gray-50' : ''}`} data-testid={`member-row-${i}`}>
                <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {(m.name || m.email || '?')[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">{m.name || m.email}</p>
                  <p className="text-xs text-gray-500 truncate">{m.email}{m.since ? ` · member since ${m.since.slice(0, 10)}` : ''}</p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {m.plans.slice(0, 3).map((p, j) => (
                    <span key={j} className={`px-2 py-1 rounded-full text-[11px] font-semibold ${STATUS_CLS[p.status] || STATUS_CLS.pending}`}>
                      {p.serviceName} · {p.intervalLabel}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* ── Announcements ── */}
      {subTab === 'announcements' && (
        <div className="grid gap-6 lg:grid-cols-5">
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-[0_4px_20px_rgba(2,12,27,0.06)] p-5 h-fit" data-testid="announcement-composer">
            <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><Megaphone className="w-4 h-4 text-blue-600" /> New announcement</h3>
            <Input value={annTitle} onChange={(e) => setAnnTitle(e.target.value)} placeholder="e.g. Regatta weekend schedule" maxLength={255} data-testid="announcement-title-input" />
            <Textarea value={annBody} onChange={(e) => setAnnBody(e.target.value)} rows={4} maxLength={8000} className="mt-2.5"
              placeholder="What should your members know?" data-testid="announcement-body-input" />
            <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
              <input type="checkbox" checked={annEmail} onChange={(e) => setAnnEmail(e.target.checked)} className="mt-0.5 accent-blue-600" data-testid="announcement-email-checkbox" />
              <span className="text-xs text-gray-600">
                <span className="font-semibold text-gray-800 flex items-center gap-1"><Mail className="w-3 h-3" /> Also email all active members</span>
                {members ? `${members.activeCount} member${members.activeCount === 1 ? '' : 's'} will get this in their inbox.` : ''}
              </span>
            </label>
            <Button onClick={postAnnouncement} disabled={annBusy} className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white" data-testid="announcement-post-btn">
              {annBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (annEmail ? 'Post & email members' : 'Post announcement')}
            </Button>
          </div>

          <div className="lg:col-span-3 space-y-3" data-testid="announcements-list">
            {announcements === null ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            : announcements.length === 0 ? (
              <div className="bg-gray-50 rounded-2xl p-10 text-center">
                <Megaphone className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No announcements yet — your members will see them in their Members Arena.</p>
              </div>
            ) : announcements.map((a) => (
              <div key={a.id} className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(2,12,27,0.06)] p-4" data-testid={`announcement-row-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                      {a.pinned && <Pin className="w-3.5 h-3.5 text-amber-500" />}
                      {a.title}
                    </p>
                    {a.body && <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">{a.body}</p>}
                    <p className="text-[11px] text-gray-400 mt-2">
                      {a.createdAt ? a.createdAt.slice(0, 10) : ''}
                      {a.emailedAt ? ` · emailed to ${a.emailRecipientCount} members` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => togglePin(a)} className={`p-1.5 rounded-lg hover:bg-gray-100 ${a.pinned ? 'text-amber-500' : 'text-gray-400'}`} title={a.pinned ? 'Unpin' : 'Pin to top'} data-testid={`announcement-pin-btn-${a.id}`}>
                      <Pin className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => removeAnnouncement(a)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" data-testid={`announcement-delete-btn-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Event Calendar ── */}
      {subTab === 'events' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">Specific-date happenings — regattas, courses, open days. Link a service for online booking, or collect free RSVPs.</p>
            <Button onClick={openCreateEvent} className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0" data-testid="new-event-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New event
            </Button>
          </div>
          {events === null ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          : events.length === 0 ? (
            <div className="bg-gray-50 rounded-2xl p-10 text-center" data-testid="events-empty-state">
              <CalendarDays className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No events yet — add your first regatta, course or open day.</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="events-list">
              {events.map((e) => (
                <div key={e.id} className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(2,12,27,0.06)] p-4" data-testid={`event-row-${e.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                        {e.membersOnly && <Lock className="w-3.5 h-3.5 text-amber-500" />}
                        {e.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{fmtDate(e.startDate)}{e.endDate && e.endDate !== e.startDate ? ` – ${fmtDate(e.endDate)}` : ''}</span>
                        {e.startTime && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{e.startTime}{e.endTime ? `–${e.endTime}` : ''}</span>}
                        {e.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</span>}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {e.category && <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold">{e.category}</span>}
                        {e.serviceName
                          ? <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold">Books via {e.serviceName}</span>
                          : (
                            <button onClick={() => openRsvps(e)}
                              className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-semibold hover:bg-gray-200 transition-colors"
                              title="View who's coming" data-testid={`event-rsvps-btn-${e.id}`}>
                              {e.rsvpCount} RSVP{e.rsvpCount === 1 ? '' : 's'}{e.capacity ? ` / ${e.capacity}` : ''}
                            </button>
                          )}
                      </div>
                    </div>
                    <ServiceToggle checked={e.active} onChange={() => toggleEventActive(e)} data-testid={`event-active-toggle-${e.id}`} />
                  </div>
                  <div className="flex items-center justify-end gap-1 mt-2">
                    <button onClick={() => openEditEvent(e)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" data-testid={`event-edit-btn-${e.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => removeEvent(e)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" data-testid={`event-delete-btn-${e.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Event create/edit dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
          <DialogTitle>{editingEvent ? 'Edit event' : 'New event'}</DialogTitle>
          <DialogDescription className="text-sm text-gray-500 -mt-1">
            Shows on your public event calendar. Link a service to take bookings, or leave unlinked to collect free RSVPs.
          </DialogDescription>
          <div className="space-y-3.5 mt-1">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Event title</label>
              <Input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="e.g. Wednesday Night Pursuit Race" maxLength={255} data-testid="event-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Category <span className="text-gray-400 font-normal">(optional)</span></label>
                <Input value={eventForm.category} onChange={(e) => setEventForm({ ...eventForm, category: e.target.value })}
                  placeholder="Racing, Courses…" maxLength={100} data-testid="event-category-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Location <span className="text-gray-400 font-normal">(optional)</span></label>
                <Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                  placeholder="Marina clubhouse" maxLength={255} data-testid="event-location-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Start date</label>
                <DateField value={eventForm.startDate} onChange={(v) => setEventForm({ ...eventForm, startDate: v })}
                  placeholder="Pick a date" testId="event-start-date-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">End date <span className="text-gray-400 font-normal">(multi-day)</span></label>
                <DateField value={eventForm.endDate} onChange={(v) => setEventForm({ ...eventForm, endDate: v })}
                  placeholder="Same day" testId="event-end-date-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Start time</label>
                <Input type="time" value={eventForm.startTime} onChange={(e) => setEventForm({ ...eventForm, startTime: e.target.value })} data-testid="event-start-time-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">End time</label>
                <Input type="time" value={eventForm.endTime} onChange={(e) => setEventForm({ ...eventForm, endTime: e.target.value })} data-testid="event-end-time-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Description <span className="text-gray-400 font-normal">(optional)</span></label>
              <Textarea value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                rows={2} maxLength={4000} placeholder="What to expect, who it's for…" data-testid="event-description-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Bookable service <span className="text-gray-400 font-normal">(optional — adds a "Book online" button)</span></label>
              <NativeSelect value={eventForm.serviceId} onChange={(e) => setEventForm({ ...eventForm, serviceId: e.target.value })} data-testid="event-service-select">
                <option value="">No booking — free RSVP event</option>
                {services.filter((s) => s.active !== false).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </NativeSelect>
            </div>
            {!eventForm.serviceId && (
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">RSVP capacity <span className="text-gray-400 font-normal">(blank = unlimited)</span></label>
                <Input type="number" min={1} value={eventForm.capacity} onChange={(e) => setEventForm({ ...eventForm, capacity: e.target.value })} data-testid="event-capacity-input" />
              </div>
            )}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <ServiceToggle checked={eventForm.membersOnly} onChange={(v) => setEventForm({ ...eventForm, membersOnly: v })} data-testid="event-members-only-toggle" />
                <span className="text-sm text-gray-600 flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-gray-400" /> Members only</span>
              </div>
              <div className="flex items-center gap-2">
                <ServiceToggle checked={eventForm.active} onChange={(v) => setEventForm({ ...eventForm, active: v })} data-testid="event-form-active-toggle" />
                <span className="text-sm text-gray-600">Live</span>
              </div>
            </div>
            <Button onClick={saveEvent} disabled={eventBusy} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="event-save-btn">
              {eventBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingEvent ? 'Save changes' : 'Add to calendar')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Event RSVP list dialog */}
      <Dialog open={!!rsvpListEvent} onOpenChange={(o) => { if (!o) setRsvpListEvent(null); }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="event-rsvps-dialog">
          <DialogTitle>RSVPs — {rsvpListEvent?.title}</DialogTitle>
          <DialogDescription className="text-sm text-gray-500 -mt-1">
            {rsvpListEvent ? `${rsvpListEvent.rsvpCount || 0} going${rsvpListEvent.capacity ? ` · capacity ${rsvpListEvent.capacity}` : ''}` : ''}
          </DialogDescription>
          {rsvpList === null ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : rsvpList.length === 0 ? (
            <div className="bg-gray-50 rounded-2xl p-8 text-center" data-testid="event-rsvps-empty">
              <Users className="w-7 h-7 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No RSVPs yet.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="event-rsvps-list">
              {rsvpList.map((r) => (
                <div key={r.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3.5 py-2.5" data-testid={`event-rsvp-row-${r.id}`}>
                  <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {(r.name || r.email || '?')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate flex items-center gap-1.5">
                      {r.name || r.email}
                      {r.isMember && <span className="px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-bold flex-shrink-0">Member</span>}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{r.email}</p>
                  </div>
                  <span className="text-xs text-gray-500 flex-shrink-0 flex items-center gap-1"><Users className="w-3 h-3" />{r.partySize}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MembersTab;
